<template>
  <Widget
    widgetName="non-validated-impacts"
    hideTimeMenu
    :customParams="customParams"
    @dataFetched="handleDataFetched"
    @locationOptionChange="handleLocationOptionChange"
  >
    <template v-slot:header-action v-if="showImperialSystemButton">
      <div class="me-4">
        <ImperialSystemButton
          :imperial="system === 'imperial'"
          @click="handleSystemChange"
        />
      </div>
    </template>

    <template v-slot:content>
      <div class="d-flex flex-column h-100 align-center justify-center">
        <div class="w-100 px-3">
          <v-row class="ma-0 pa-0 justify-center">
            <v-col
              class="d-flex"
              style="max-width: 173px"
              :cols="cols"
              v-for="(impact, index) in potentialImpacts"
              :key="impact.metric + index"
            >
              <Impact
                :metric="impact.metric"
                :total="impact.total"
                :unit="impact.unit"
                :system="system"
              />
            </v-col>
          </v-row>
        </div>
      </div>
    </template>

    <template v-slot:widget-actions>
      <Button
        color="primary"
        data-testid="potentialImpacts-button-exploreRevs"
        data-tourid="potential-impacts-explore-revs"
        plain
        size="small"
        :text="$t('insights.explore_revs')"
        @click="goToRevs"
      />
      <v-divider vertical class="mx-2" />
      <Button
        color="primary"
        data-testid="potentialImpacts-button-viewMoreMetrics"
        data-tourid="potential-impacts-view-more-metrics"
        icon="mdi-arrow-right"
        iconPosition="right"
        plain
        size="small"
        :text="$t('insights.view_more_metrics')"
        @click="goToReport"
      />
    </template>
  </Widget>
</template>

<script>
import { mapGetters } from 'vuex'

import Button from '@/components/UI/Button.vue'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import Widget from '../SharedComponents/WidgetBase.vue'
import Impact from './Impact.vue'
import ImperialSystemButton from './ImperialSystemButton.vue'

export default {
  name: 'PotentialImpacts',

  mixins: [mixin, trackerMixin],

  components: {
    Button,
    Widget,
    Impact,
    ImperialSystemButton,
  },

  data() {
    return {
      customParams: { limit: 6 },
      selectedSiteId: null,
      potentialImpacts: [],
      system: 'metric',
      systemConvertibleUnits: ['milligram', 'cubic_inch'],
    }
  },

  computed: {
    ...mapGetters(['organizationId']),

    cols() {
      const length = this.potentialImpacts.length

      if ([1, 2, 4].includes(length) || this.$vuetify.breakpoint.xs) return 6

      return 4
    },

    showImperialSystemButton() {
      return this.systemConvertibleUnits.some(unit => this.units.includes(unit))
    },

    units() {
      return this.potentialImpacts.map(impact => impact.unit)
    },
  },

  methods: {
    goToReport() {
      this.trackInsightsWidgetNavigate({
        widget: 'non-validated-impacts',
        action: 'viewMoreMetricsClick',
        selectedSite: this.selectedSiteId,
      })

      const query = {
        organization: this.organizationId,
        type: 'non_validated_impacts',
      }

      if (this.selectedSiteId !== 'global') {
        query.sites = [this.selectedSiteId]
      }

      this.openInNewTab({
        name: 'reports',
        query,
      })
    },

    goToRevs() {
      this.trackInsightsWidgetNavigate({
        widget: 'non-validated-impacts',
        action: 'exploreRevsClick',
        selectedSite: this.selectedSiteId,
      })

      const query = {
        types: ['improvementIdea', 'quickAction', 'problemSolving'],
        status: ['completed'],
        hasImplementationValidation: true,
      }

      if (this.selectedSiteId !== 'global') {
        query.sites = [this.selectedSiteId]
      }

      this.openInNewTab({
        name: 'explore',
        query,
      })
    },

    handleSystemChange() {
      this.trackInsightsWidgetNavigate({
        widget: 'non-validated-impacts',
        action: 'changeMetricSystem',
        system: this.system,
      })

      this.system = this.system === 'metric' ? 'imperial' : 'metric'
    },

    handleLocationOptionChange(option) {
      this.selectedSiteId = option
    },

    handleDataFetched(widget) {
      this.potentialImpacts = [...widget.data]
    },
  },
}
</script>
<style lang="scss">
.v-divider.theme--light.divider-light {
  border-color: $color-blue-grey-lighten3 !important;
}
</style>
